import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import {SaveOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import Message from '../../../shared/message/Message.js';
import {CampoSelect} from '../../components/processo/CamposProcesso.jsx';
import {MENSAGEM_CURSO_DUPLICADO, vagaDuplicada} from '../../components/processo/processoSelecao.js';

const FormSchema = Yup.object().shape({
    idCampus: Yup.string().required('É obrigatório'),
    idCurso: Yup.string().required('É obrigatório'),
    idTurno: Yup.string().test('turno', 'É obrigatório', function turno(valor) {
        const cursoPresencial = this.options.context?.cursoPresencial;
        return !(cursoPresencial && cursoPresencial(this.parent.idCurso)) || !!valor;
    })
});

const VALORES_VAZIOS = {idCampus: '', idCurso: '', idTurno: ''};

const texto = valor => (valor === null || valor === undefined ? '' : String(valor));

const paraFormulario = vaga => (vaga ? {
    idCampus: texto(vaga.campus?.id),
    idCurso: texto(vaga.curso?.id),
    idTurno: texto(vaga.turno?.id)
} : VALORES_VAZIOS);

const useStyles = makeStyles(theme => ({
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    mensagem: {
        marginBottom: theme.spacing(2)
    }
}));

/*
 * Curso permitido no processo de seleção (_formSelecionarVaga do PrimeFaces):
 * campus, curso (dos cursos do programa e dos processos vestibulares) e,
 * para curso presencial, o turno — sem turno a vaga é EAD. Campus + curso +
 * turno repetido na lista é recusado aqui mesmo.
 */
export default function DialogVagaProcesso({open, onClose, vaga, indice = -1, vagas = [], campi = [], cursos = [], turnos = [], onSalvar}) {
    const classes = useStyles();
    const [mensagem, setMensagem] = useState('');

    // O curso da vaga em edição continua na lista mesmo que o apoio não o traga mais.
    const opcoesCursos = vaga?.curso?.id && !cursos.some(curso => String(curso.id) === String(vaga.curso.id))
        ? [vaga.curso, ...cursos]
        : cursos;

    const cursoPorId = id => opcoesCursos.find(curso => String(curso.id) === String(id)) || null;

    // O turno só é exigido para curso presencial: a lista de cursos atual vai no contexto da validação.
    const contextoValidacao = useRef({});
    contextoValidacao.current = {cursoPresencial: idCurso => !!cursoPorId(idCurso)?.presencial};

    const {control, handleSubmit, reset, watch, setValue, formState: {errors, isSubmitting}} = useForm({
        resolver: (valores, contexto, opcoes) => yupResolver(FormSchema)(valores, contextoValidacao.current, opcoes),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => {
        reset(paraFormulario(vaga));
        setMensagem('');
    };

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, vaga]);

    const cursoSelecionado = cursoPorId(watch('idCurso'));
    const presencial = !!cursoSelecionado?.presencial;

    /* Curso EAD não tem turno. */
    const aoTrocarCurso = idCurso => {
        if (!cursoPorId(idCurso)?.presencial) {
            setValue('idTurno', '');
        }
    };

    const submit = valores => {
        const campus = campi.find(item => String(item.id) === String(valores.idCampus));
        const curso = cursoPorId(valores.idCurso);
        const turno = presencial ? turnos.find(item => String(item.id) === String(valores.idTurno)) : null;
        const nova = {
            ...(vaga || {}),
            id: vaga?.id ?? null,
            campus: {id: campus.id, nome: campus.nome},
            curso: {id: curso.id, descricao: curso.descricao, codigo: curso.codigo, presencial: !!curso.presencial},
            turno: turno ? {id: turno.id, descricao: turno.descricao} : null
        };
        if (vagaDuplicada(vagas, nova, indice)) {
            setMensagem(MENSAGEM_CURSO_DUPLICADO);
            return;
        }
        onSalvar(nova);
        onClose();
    };

    /* O diálogo fica dentro do form da página na árvore do React: o submit não pode subir até ele. */
    const aoEnviar = evento => {
        evento.stopPropagation();
        handleSubmit(submit)(evento);
    };

    const props = {control, errors};

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering: preencher}} scroll='body'
                aria-labelledby='dialog-vaga-processo-titulo' classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-vaga-processo-titulo'>{vaga ? 'Editar Curso' : 'Adicionar Curso'}</DialogTitle>
            <form noValidate autoComplete='off' onSubmit={aoEnviar}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    {!!mensagem && (
                        <div className={classes.mensagem}>
                            <Message type='error' variant='standard'>{mensagem}</Message>
                        </div>
                    )}
                    <Grid container spacing={3}>
                        <CampoSelect {...props} nome='idCampus' rotulo='Campus *' sm={12}
                                     opcoes={campi.map(item => ({valor: String(item.id), descricao: item.nome}))}/>
                        <CampoSelect {...props} nome='idCurso' rotulo='Curso *' sm={12} onAlterar={aoTrocarCurso}
                                     opcoes={opcoesCursos.map(item => ({
                                         valor: String(item.id),
                                         // O back já manda o rótulo do JSF ("DESCRIÇÃO (CÓDIGO)"); sem ele, monta igual.
                                         descricao: item.rotulo || (item.codigo ? `${item.descricao || ''} (${item.codigo})` : item.descricao)
                                     }))}/>
                        {presencial && (
                            <CampoSelect {...props} nome='idTurno' rotulo='Turno *' sm={12}
                                         opcoes={turnos.map(item => ({valor: String(item.id), descricao: item.descricao}))}/>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={onClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>Salvar</ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogVagaProcesso.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Vaga em edição; nula ao adicionar. */
    vaga: PropTypes.object,
    /* Posição da vaga em edição na lista (-1 ao adicionar). */
    indice: PropTypes.number,
    vagas: PropTypes.array,
    campi: PropTypes.array,
    cursos: PropTypes.array,
    turnos: PropTypes.array,
    onSalvar: PropTypes.func.isRequired
};
